import { AxiosResponse } from 'axios'
import { queryOptions } from '@tanstack/react-query'
import i18n from '../i18n/i18n'
import { NUnits } from '../interfaces/services/units.interfaces'
import { createUrl } from './helpers'
import axiosAuth from './config'
import { sortStringsAlphabetically } from '../helpers/sorting'
import { IUnitModel } from '../interfaces/models/units.interfaces'

function translateUnassignedUnit(unit: IUnitModel): void {
  if (unit.isUnassigned) {
    // eslint-disable-next-line no-param-reassign
    unit.title = i18n.t('common.unit.unassigned')
    // eslint-disable-next-line no-param-reassign
    unit.titleLegal = i18n.t('common.unit.unassigned')
  }
}

export const getUnitsPerSociety = (
  societyId: string
): Promise<AxiosResponse<NUnits.NGetPerSocieties.IResponse>> => {
  return axiosAuth.get<NUnits.NGetPerSocieties.IResponse>(
    createUrl(`/units/get-per-society/${societyId}`)
  )
}

export const getUserUnits = (
  userId: string
): Promise<AxiosResponse<NUnits.NGetUserUnits.IResponse>> => {
  return axiosAuth.get<NUnits.NGetUserUnits.IResponse>(
    createUrl(`/units/get-user-units/${userId}`)
  )
}

export const getUnit = (
  id: string
): Promise<AxiosResponse<NUnits.NGetById.IResponse>> => {
  return axiosAuth.get<NUnits.NGetById.IResponse>(
    createUrl(`/units/unit/${id}`)
  )
}

export const patchUnit = (params: {
  id: string
  body: NUnits.NPatch.IRequestBody
}): Promise<AxiosResponse<NUnits.NPatch.IResponse>> => {
  return axiosAuth.patch<NUnits.NPatch.IResponse>(
    createUrl(`/units/unit/${params.id}`),
    params.body
  )
}

export const createUnit = (
  body: NUnits.NCreate.IRequestBody
): Promise<AxiosResponse<NUnits.NCreate.IResponse>> => {
  return axiosAuth.post<NUnits.NCreate.IResponse>(
    createUrl(`/units/unit`),
    body
  )
}

export const deleteUnit = (
  id: string
): Promise<AxiosResponse<NUnits.NDel.IResponse>> => {
  return axiosAuth.delete<NUnits.NDel.IResponse>(createUrl(`/units/unit/${id}`))
}

export const getUnitEstimate = (
  id: string
): Promise<AxiosResponse<NUnits.NEstimate.IResponse>> => {
  return axiosAuth.get<NUnits.NEstimate.IResponse>(
    createUrl(`/units/unit/${id}/estimate`)
  )
}

export const getUnitTrend = (
  id: string
): Promise<AxiosResponse<NUnits.NTrend.IResponse>> => {
  return axiosAuth.get<NUnits.NTrend.IResponse>(
    createUrl(`/units/unit/${id}/trend`)
  )
}

export const unitQueries = {
  getUnit: (unitId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'unit', entity: 'detail', unitId }],
      queryFn: async ({ queryKey }) => {
        const unit = (await getUnit(queryKey[0].unitId!)).data.data
        translateUnassignedUnit(unit)
        return unit
      },
      enabled: !!unitId,
    }),
  getUserUnits: (userId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'unit', entity: 'list', userId }],
      queryFn: async ({ queryKey }) => {
        const units = (await getUserUnits(queryKey[0].userId!)).data.data
        units.sort((a, b) =>
          sortStringsAlphabetically(a.titleLegal, b.titleLegal)
        )

        units.forEach((unit) => {
          translateUnassignedUnit(unit)
        })
        return units
      },
      enabled: !!userId,
    }),
  getUnitsPerSociety: (societyId?: string) =>
    queryOptions({
      queryKey: [{ scope: 'unit', entity: 'list', societyId }],
      queryFn: async ({ queryKey }) => {
        const units = (await getUnitsPerSociety(queryKey[0].societyId!)).data
          .data

        units.sort((a, b) =>
          sortStringsAlphabetically(a.titleLegal, b.titleLegal)
        )

        units.forEach((unit) => {
          translateUnassignedUnit(unit)
        })

        return units
      },
      enabled: !!societyId,
    }),
}
